import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import axiosInstance from './axiosInstance';

const humanReadableSize = (sizeInBytes = 0) => {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) return '0 KB';
  const sizeInKb = sizeInBytes / 1024;
  if (sizeInKb < 1024) {
    return `${sizeInKb.toFixed(2)} KB`;
  }
  return `${(sizeInKb / 1024).toFixed(2)} MB`;
};

const parseErrorMessage = async (error) => {
  const fallback = 'Failed to upload the finance report.';
  if (!error?.response) {
    return error?.message || fallback;
  }

  const { data } = error.response;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) return parsed.error;
        if (parsed?.message) return parsed.message;
        if (Array.isArray(parsed?.errors) && parsed.errors.length) {
          const first = parsed.errors[0];
          return first?.error || first?.message || fallback;
        }
      } catch (parseErr) {
        return text || fallback;
      }
    } catch (blobErr) {
      return fallback;
    }
  }

  return error.response?.data?.error || error.response?.data?.message || fallback;
};

const FinanceReportUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadSummary, setUploadSummary] = useState(null);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections?.length) {
      setErrorMessage('Only .csv files up to the configured size limit are supported.');
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;
    setSelectedFile(file);
    setSuccessMessage('');
    setErrorMessage('');
    setUploadSummary(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Please choose a finance report CSV before uploading.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    setIsUploading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await axiosInstance.post('/api/v1/financial-sales', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const summary = response?.data?.data ?? response?.data ?? null;
      setUploadSummary(summary);
      const successCount = Array.isArray(summary?.successful_records)
        ? summary.successful_records.length
        : 0;
      const failedUploadCount = Array.isArray(summary?.failed_upload_records)
        ? summary.failed_upload_records.length
        : 0;
      const failedCount = failedUploadCount;
      const totalRows = summary?.total_rows || successCount + failedCount;
      const jobsUpdated = summary?.jobs_updated || 0;
      setSuccessMessage(
        `Upload completed. ${successCount} successful, ${failedCount} failed. Total rows: ${totalRows}. Updated jobs: ${jobsUpdated}.`
      );
      setSelectedFile(null);
    } catch (error) {
      const message = await parseErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setSuccessMessage('');
    setErrorMessage('');
    setUploadSummary(null);
  };

  const createdDocumentNumbers = new Set(
    Array.isArray(uploadSummary?.financial_sales_created) ? uploadSummary.financial_sales_created : []
  );
  const updatedDocumentNumbers = new Set(
    Array.isArray(uploadSummary?.financial_sales_updated) ? uploadSummary.financial_sales_updated : []
  );

  const successfulRecords = Array.isArray(uploadSummary?.successful_records)
    ? uploadSummary.successful_records
    : [];

  const createdRows = successfulRecords.filter((item) => createdDocumentNumbers.has(item?.document_number));
  const updatedRows = successfulRecords.filter((item) => updatedDocumentNumbers.has(item?.document_number));

  const failedRows = [
    ...(Array.isArray(uploadSummary?.failed_records) ? uploadSummary.failed_records.map((item) => ({
      row_number: item?.row_number,
      document_number: item?.document_number,
      job_number: item?.job_number,
      doc_type_id: item?.record?.doc_type_id || null,
      reason: item?.reason || 'Row failed validation',
    })) : []),
    ...(Array.isArray(uploadSummary?.failed_upload_records) ? uploadSummary.failed_upload_records.map((item) => ({
      row_number: item?.row_number,
      document_number: item?.record?.document_number,
      job_number: item?.record?.job_number,
      doc_type_id: item?.record?.doc_type_id,
      reason: item?.error || 'Database write failed',
    })) : []),
  ];

  const renderResultTable = (title, rows, emptyText, includeReason = false) => (
    <Box sx={{ mt: 3, width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {title} ({rows.length})
      </Typography>
      <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Row Number</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Document Number</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Job Number</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Document Type</TableCell>
              {includeReason && <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? rows.map((item, index) => (
              <TableRow key={`${title}-${item?.row_number || 'unknown'}-${item?.document_number || index}-${index}`}>
                <TableCell>{item?.row_number || 'unknown'}</TableCell>
                <TableCell>{item?.document_number || 'unknown'}</TableCell>
                <TableCell>{item?.job_number || 'unknown'}</TableCell>
                <TableCell>{item?.doc_type_id || 'unknown'}</TableCell>
                {includeReason && <TableCell>{item?.reason || 'Unknown failure'}</TableCell>}
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={includeReason ? 5 : 4}>{emptyText}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: 'auto',
        px: '5%',
        py: 4,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'left', width: '100%' }}>
        Upload Finance Report
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Upload the finance report CSV so it can be saved for report generation.
      </Typography>

      <Box
        {...getRootProps({ className: 'dropzone' })}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: isDragActive ? 'primary.light' : '#fff',
          cursor: 'pointer',
          width: '100%',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop the CSV file here...' : 'Drag & drop the CSV file here'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          or click to select a file
        </Typography>
      </Box>

      {selectedFile && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="info">
            Selected file: {selectedFile.name} ({humanReadableSize(selectedFile.size)})
          </Alert>
        </Box>
      )}

      {successMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="success">{successMessage}</Alert>
        </Box>
      )}

      {errorMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={isUploading || !selectedFile}
        >
          {isUploading ? <CircularProgress size={24} color="inherit" /> : 'Upload CSV'}
        </Button>
        {selectedFile && (
          <Button variant="outlined" color="secondary" onClick={handleClear} disabled={isUploading}>
            Clear
          </Button>
        )}
      </Box>

      {uploadSummary && (
        <>
          {renderResultTable('Created Records', createdRows, 'No records were created.')}
          {renderResultTable('Updated Records', updatedRows, 'No records were updated.')}
          {renderResultTable('Failed Records', failedRows, 'No failed rows.', true)}
        </>
      )}
    </Box>
  );
};

export default FinanceReportUpload;
